import { notification } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { useModalForm } from '../../../../foundation/hooks';
import { useGetVariantTypeOptionsQuery } from '../../../api/variantCheckApi';
import { VARIANT_STAGE_TYPES } from '../../../variant_workflow/constants/stages';
import { Variant } from '../../redux/types';

interface UseVariantCreateMetadataModalLogicProps {
  closeModal: () => void;
  variantMetaData?: Variant | null;
  handleVariantCreate?: (variant_type: string) => Promise<{
    variant_id: number;
    created: boolean;
    message: string;
  }>;
}

/**
 * Custom hook that encapsulates all the business logic for VariantCreateMetadataModal.
 * Separates concerns by extracting state management, selectors, and handlers
 * from the UI component.
 */
export const useVariantCreateMetadataModalLogic = ({
  closeModal,
  variantMetaData,
  handleVariantCreate,
}: UseVariantCreateMetadataModalLogicProps) => {
  const navigate = useNavigate();
  // The query hook fetches once and serves every later call from cache
  // (keepUnusedDataFor: Infinity — same pattern as globalDataApi).
  const { data: variantTypeOptions = [] } = useGetVariantTypeOptionsQuery();
  const [selectedVariantType, setSelectedVariantType] = useState<
    string | undefined
  >(variantMetaData?.variant_type);

  const { isLoading, setIsLoading, createModalOpts } = useModalForm({
    onClose: closeModal,
    successMessage: 'Variant created successfully',
    stageType: VARIANT_STAGE_TYPES.CLINICAL_OBSERVATION,
  });

  useEffect(() => {
    // initialize when metadata changes
    setSelectedVariantType(variantMetaData?.variant_type);
  }, [variantMetaData?.variant_type]);

  // createModalOpts comes from useModalForm (global) – no local override needed.

  const variantKeyValues = useMemo(() => {
    const variantData = variantMetaData;
    return [
      {
        label: 'Gene',
        value: `${variantData?.gene_symbol} ${variantData?.gene_name ? `(${variantData?.gene_name})` : ''}`,
      },
      {
        label: 'Transcript',
        value: `${variantData?.transcript_external_id} ${variantData?.mane ? `(${variantData?.mane})` : ''}`,
      },
      {
        label: 'cDNA',
        value: variantData?.cdna,
      },
      {
        label: 'Variant type',
        value: selectedVariantType || variantData?.variant_type, // editable below
      },
      {
        label: 'Protein change',
        value: `${variantData?.protein_change || ''} / ${variantData?.protein_change_single_letter || ''}`,
      },
      {
        label: 'Consequence',
        value: variantData?.consequence,
      },
      {
        label: 'Start-end (length)',
        value: `${variantData?.genomic_location_start || ''} - ${variantData?.genomic_location_end || ''}${
          variantData?.length_calculated
            ? `(${variantData?.length_calculated})`
            : ''
        }`,
      },
      {
        label: 'Chromosome',
        value: variantData?.chromosome_symbol,
      },
      {
        label: 'Affected exon',
        value: variantData?.affected_exon,
      },
      {
        label: 'gHGVS GRCh37',
        value: variantData?.hgvs_genomic_hg19,
      },
      {
        label: 'gHGVS GRCh38',
        value: variantData?.hgvs_genomic,
      },
      {
        label: 'HGVS protein',
        value: variantData?.hgvs_protein,
      },
      {
        label: 'VCF string',
        value: variantData?.vcf_string,
      },
    ];
  }, [variantMetaData, selectedVariantType]);

  const handleSaveChanges = async () => {
    try {
      setIsLoading(true);
      if (!selectedVariantType) {
        notification.error({
          message: 'Variant type required',
          description: 'Please select a variant type before saving.',
        });
        setIsLoading(false);
        return;
      }
      const result = await handleVariantCreate?.(selectedVariantType);

      if (result?.created) {
        notification.success({
          message: 'Success!',
          description: result.message || 'Variant created successfully',
        });
      } else {
        notification.info({
          message: 'Info',
          description: result?.message || 'Variant already exists',
        });
      }

      if (!result?.variant_id) {
        throw new Error('Variant creation did not return a variant ID.');
      }

      setIsLoading(false);
      closeModal();
      await navigate(`/variant-workflow/${result.variant_id}`);
    } catch (error) {
      setIsLoading(false);
      notification.error({
        message: 'Variant could not be saved',
        description:
          error instanceof Error
            ? error.message
            : 'Please try again or contact support.',
      });
    }
  };

  return {
    isLoading,
    variantKeyValues,
    createModalOpts,
    handleSaveChanges,
    variantTypeOptions,
    selectedVariantType,
    setSelectedVariantType,
  };
};

export default useVariantCreateMetadataModalLogic;
